import React, { useState, useEffect } from "react";
import img from "../assets/images/notification.svg";
import { links, patientRecordsFields, clinicalStudiesFields } from "../stories/Forms/fields";
import { useAuth } from "../components/AuthContext/AuthContext";
import LoggedInLayout from "../stories/LoggedInScreens/LoggedInLayout";
import logo from "../assets/images/boots-myhealthchecked-logo.svg";
import formatData, {
  formatFullName,
  formatStudyName,
  formatTestType,
  formatReportedDate,
  STATUS_MAP
} from "../utils/utils";
import { dateValidationSchema } from "../stories/Forms/validationSchema";
import { searchBookingsUrl, checkClinicalPatientUrl } from "../utils/constants";
import useApis from "../services/useAPI";
import SearchForm from "../stories/Forms/SearchForm";
import Table from "../stories/Tables/Table";
import DuplicateIdTable from "../stories/Tables/DuplicateIdTable";
import { Primary } from "../stories/Buttons/Button.stories";
import down from "../assets/icons/chevron.svg";
import up from "../assets/icons/chevron-up.svg";
import { XCircleFill, InfoCircleFill } from "react-bootstrap-icons";

const PAGE_TITLE = (
  <>
    Patient Status Viewer{" "}
    <span className="sub-text">
      (status search only, no processing workflows)
    </span>
  </>
);

// ─── Pure Helpers ─────────────────────────────────────────────────────────────

const getStatusDetails = (status) => {
  if (!status) return { className: "", label: "" };
  const key = status.toLowerCase().replace(/\s+/g, "-");
  return STATUS_MAP[key] ?? { className: "", label: status };
};

const isHoverDisabled = (status, phlebotomistBookingId) =>
  status === "registered" ||
  status === "created" ||
  (status === "activating" && phlebotomistBookingId !== "");

// ─── Component ────────────────────────────────────────────────────────────────

const Search = () => {
  const location = globalThis.location;
  const isSearchPage = location?.pathname === "/search";

  // ── State ──────────────────────────────────────────────────────────────────
  const [flattenedBookings, setFlattenedBookings] = useState([]);
  const [usersData, setUsersData] = useState([]); // holds multiple users when duplicate found
  const [selectedUser, setSelectedUser] = useState(null); // user selected from users table
  const [showSearch, setShowSearch] = useState(true);
  const [initialUsers, setInitialUsers] = useState([]);
  const auth = useAuth();
  const { callApi, resultPost, serverError, tokenError } = useApis();

  const selectedService = localStorage.getItem("selectedService");

  useEffect(() => {
    auth.setTitle(PAGE_TITLE);
  }, []);

  useEffect(() => {
    if (serverError || tokenError) {
      auth?.showToast(
        "Error",
        tokenError || serverError,
        "../assets/mhc.png",
        {icon: <XCircleFill />},
      );
    }
  }, [serverError, tokenError]);

  useEffect(() => {
    if (!serverError && resultPost?.users?.length > 1) {
      // Multiple users found — show users table inline instead of navigating
      setUsersData(resultPost.users);
      setInitialUsers(resultPost.users);
      setSelectedUser(null);
      setFlattenedBookings([]);
    } else if (resultPost?.bookings) {
      // Single user or bookings returned after user selection
      setFlattenedBookings(resultPost.bookings);
      setUsersData([]);
    }
  }, [resultPost]);

  // ── Booking Helpers ────────────────────────────────────────────────────────

  const modifyBookings = (bookings) =>
    bookings.map((booking, index) => {
      const hover = isHoverDisabled(
        booking.status,
        booking.phlebotomist_booking_id,
      );
      const { className, label } = getStatusDetails(booking?.status);

      return {
        id: index + 1,
        bookingId: booking.booking_id,
        testType: booking.test_type,
        sampleType: booking.sample_type,
        reportedDate: booking.reported_date
          ? formatReportedDate(booking.reported_date)
          : null,
        doctor: booking.reviewer_name ?? " ",
        status: {
          component: (
            <Primary
              hover={hover}
              label={label}
              size="small"
              variant={className}
            />
          ),
        },
      };
    });

  // ── Users Table Helpers ────────────────────────────────────────────────────

  /**
   * Shapes users array for DuplicateIdTable.
   * Mirrors the modifyDuplicateIdBookings pattern from UserDetails.
   * The "Select" button triggers handleUserSelect via onSubmit prop.
   */
  const modifyDuplicateUsers = (users) =>
    users.map((user) => ({
      id: user?.user_id_annotation,
      bookingId: user.booking_id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      dob: user.dob,
      status: {
        variant: "primary",
        raw: user.status || "",
      },
    }));

  // ── User Selection ─────────────────────────────────────────────────────────

  /**
   * Called when user clicks "Select" in the users table.
   * Fetches bookings for that specific user.
   */
  const handleUserSelect = async (user) => {
    setSelectedUser(user);
    setUsersData([]); // hide users table
    setFlattenedBookings([]); // clear any previous bookings
    await callApi(
      {
        service_type: selectedService,
        first_name: user?.first_name,
        last_name: user?.last_name,
        email: user?.email,
        phone_number_primary: user?.phone_number_primary,
        study: user?.study,
        test_type: user?.test_type,
        status_viewer: true,
      },
      searchBookingsUrl,
      "POST",
      {
        Authorization: `Bearer ${localStorage.getItem("access_token") || auth.accessToken
          }`,
        "Content-Type": "application/json",
      },
    );
    // resultPost update triggers useEffect → sets flattenedBookings
  };
  /**
   * Goes back from the bookings table to the users list.
   */
  const handleBackToUsers = () => {
    setSelectedUser(null);
    setFlattenedBookings([]);
    setUsersData(initialUsers || []);
  };

  // ── Form Helpers ───────────────────────────────────────────────────────────

  const formError =
    selectedService === "healthCare"
      ? "Please fill in at least 3 fields to submit the form"
      : "All fields are mandatory";

  const getFields = () =>
    (selectedService === "healthCare" ? patientRecordsFields : clinicalStudiesFields).map((field) => {
      if (field?.type !== "submit") return field;
      return {
        ...field,
        className: field.className
          .split(" ")
          .filter((cls) =>
            isSearchPage ? cls !== "health-care-search-btn" : true,
          )
          .join(" "),
      };
    });

  const handleSubmit = async (formValues) => {
    // Reset all table state on every new search
    setUsersData([]);
    setSelectedUser(null);
    setFlattenedBookings([]);

    const url =
      selectedService === "clinical"
        ? checkClinicalPatientUrl
        : searchBookingsUrl;

    const formattedData = {
      ...formatData(formValues),
      first_name: formatFullName(formValues.first_name),
      last_name: formatFullName(formValues.last_name),
      study: formatStudyName(formValues.study),
      test_type: formatTestType(formValues.test_type),
      service_type: selectedService,
      status_viewer: true,
    };

    await callApi(formattedData, url, "POST", {
      Authorization: `Bearer ${localStorage.getItem("access_token") || auth.accessToken
        }`,
      "Content-Type": "application/json",
    });
  };

  // ── Derived Data ───────────────────────────────────────────────────────────

  const modifiedDuplicateUsers = usersData.length
    ? modifyDuplicateUsers(usersData)
    : [];
  const modifiedBookings = flattenedBookings?.length
    ? modifyBookings(flattenedBookings)
    : [];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <LoggedInLayout
      buttonLabel="Logout"
      img={img}
      isActivation={false}
      links={links}
      logo={logo}
      pageTitle={auth.title}
      userInitials={auth.userInitials}
    >
      <div
        className={
          isSearchPage ? "search-page-wrapper patient-details-page" : ""
        }
      >
        {/* ── Search Form ── */}
        <div>
          <div className={`collapse ${showSearch ? "show" : ""}`}>
            <SearchForm
              colStructure={4}
              fieldError={true}
              fields={getFields()}
              formError={formError}
              onSubmit={handleSubmit}
              validationSchema={dateValidationSchema}
              tokenError={tokenError}
              title=""
              subtitle=""
            />
          </div>
          {serverError &&
            <div className="server-error float-end">
              <InfoCircleFill /> {serverError}
            </div>
          }
          {!serverError && resultPost?.success === false &&
            <div className="server-error float-end">
              <InfoCircleFill /> {resultPost?.message}
            </div>
          }
          <button
            aria-expanded={showSearch}
            className="btn p-0 hide-search-toggle show-hide-btn"
            onClick={() => setShowSearch((prev) => !prev)}
          >
            {showSearch ? "Hide search" : "Show search"}{" "}
            <img alt="arrow" src={showSearch ? up : down} />
          </button>
        </div>

        {/* ── Stage 1: Multiple users found — DuplicateIdTable ── */}
        {usersData.length > 1 && (
          <div className="patient-details-page multiple-user-page mt-3">
            <div className="container">
              <div className="row">
                <div className="col-md-12">
                  <h3 className="server-error text-blue mb-3 ">
                    Multiple patients are registered with these details. Please select the appropriate one from the table below.
                  </h3>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <DuplicateIdTable
                    data={modifiedDuplicateUsers}
                    onSubmit={(row) => {
                      // row is the shaped user object; find the original to pass full data
                      const original = usersData.find(
                        (u) => u.user_id_annotation === row.id,
                      );
                      handleUserSelect(original || row);
                    }}
                    pagination={true}
                    search={false}
                    sort={false}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Stage 2: Bookings for selected user ── */}
        {selectedUser && (
          <div className="">
            <div className="table-responsive">
              <Table
                data={modifiedBookings}
                pagination={true}
                search={true}
                sort={false}
              />
            </div>
          </div>
        )}

        {/* ── Default: single-user direct bookings result ── */}
        {!usersData.length && !selectedUser && (
          <div className="table-responsive">
            <Table
              data={modifiedBookings}
              pagination={true}
              search={true}
              sort={false}
            />
          </div>
        )}
      </div>
      {selectedUser && (
        <div className="d-flex">
          <Primary
            label="Back"
            size="small"
            variant="outline"
            onClick={handleBackToUsers}
            className={"btn-back btn-rounded"}
          />
        </div>
      )}
    </LoggedInLayout>
  );
};

export default Search;
